import { Todo, RecurrenceRule } from '../types/todo';
import { randomId } from './idUtils';

export function matchesRecurrence(
  rule: RecurrenceRule,
  parentDate: string,
  targetDate: string,
): boolean {
  const parent = new Date(parentDate + 'T00:00:00');
  const target = new Date(targetDate + 'T00:00:00');
  if (target <= parent) return false;

  const dayOfWeek = target.getDay();
  const diffMs = target.getTime() - parent.getTime();
  const diffDays = Math.round(diffMs / 86_400_000);

  switch (rule.type) {
    case 'daily': return true;
    case 'weekly': return (rule.days ?? []).includes(dayOfWeek);
    case 'biweekly': {
      const diffWeeks = Math.floor(diffDays / 7);
      return diffWeeks % 2 === 0 && (rule.days ?? []).includes(dayOfWeek);
    }
    case 'monthly': return target.getDate() === (rule.dayOfMonth ?? parent.getDate());
    case 'interval': {
      const every = rule.every ?? 1;
      const divisor = rule.unit === 'week' ? 7 : 1;
      return diffDays % (every * divisor) === 0;
    }
    default: return false;
  }
}

/** Returns new child todos that should be created for `date` based on recurring templates. */
export function computeNewChildren(all: Todo[], date: string): Todo[] {
  const now = new Date().toISOString();
  const created: Todo[] = [];

  const templates = all.filter(t => t.recurrenceRule && !t.recurrenceParentId);

  for (const tpl of templates) {
    if (!matchesRecurrence(tpl.recurrenceRule!, tpl.scheduledDate, date)) continue;
    const existing = all.find(t => t.recurrenceParentId === tpl.id && t.scheduledDate === date);
    if (existing) continue;

    created.push({
      id: randomId(),
      title: tpl.title,
      completed: false,
      completedAt: null,
      onComplete: tpl.onComplete,
      scheduledDate: date,
      scheduledTime: tpl.scheduledTime,
      recurrenceRule: null,
      recurrenceParentId: tpl.id,
      sortOrder: tpl.sortOrder,
      createdAt: now,
      updatedAt: now,
    });
  }
  return created;
}

/** Filters and sorts the full todos list down to what should be displayed for `date`. */
export function filterTodosForDisplay(all: Todo[], date: string): Todo[] {
  return all
    .filter(t => {
      if (t.scheduledDate !== date) return false;
      if (t.recurrenceRule && !t.recurrenceParentId) return false;
      if (t.deleted) return false;
      return true;
    })
    .sort((a, b) => {
      if (a.completed !== b.completed) return a.completed ? 1 : -1;
      if (a.scheduledTime && !b.scheduledTime) return -1;
      if (!a.scheduledTime && b.scheduledTime) return 1;
      if (a.scheduledTime && b.scheduledTime) return a.scheduledTime.localeCompare(b.scheduledTime);
      return a.sortOrder - b.sortOrder;
    });
}
